import { call, put } from 'redux-saga/effects';
import { config } from '../../api/config';
import { Api } from '../../api/api';
import { actions } from '..';
import { errorMessage, downloadFile } from '../../utils';

const mapListItem = group => ({
  id: group.id,
  name: group.name,
  status: group.status,
  createdAt: group.created_at ?? group.createdAt,
  usersCount: group.client_count ?? group.clientCount ?? 0,
});

const buildListParams = params => {
  const { page = 1, pageSize = 12, ...rest } = params || {};
  const limit = pageSize;
  const offset = (page - 1) * pageSize;
  return { ...rest, limit, offset };
};

const CLIENT_TYPE_IDS = {
  private: 91,
  business: 92,
};

const resolveClientTypeId = group => {
  const raw = group?.client_type ?? group?.clientType;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) return numeric;
    if (raw === 'private') return CLIENT_TYPE_IDS.private;
    if (raw === 'business') return CLIENT_TYPE_IDS.business;
  }
  return null;
};

const buildDeactivatePayload = (group, status = 0) => {
  const clientTypeId = resolveClientTypeId(group);
  const payload = {
    status,
  };

  if (group?.name) payload.name = group.name;
  if (clientTypeId) payload.client_type = clientTypeId;

  if (Array.isArray(group?.service_groups)) {
    payload.service_groups = group.service_groups;
  }
  if (Array.isArray(group?.payment_methods)) {
    payload.payment_methods = group.payment_methods;
  }
  if (Array.isArray(group?.location_ids)) {
    payload.location_ids = group.location_ids;
  }
  if (Array.isArray(group?.membership_ids)) {
    payload.membership_ids = group.membership_ids;
  }

  if (group?.activity_date_from !== undefined) {
    payload.activity_date_from = group.activity_date_from;
  }
  if (group?.activity_date_to !== undefined) {
    payload.activity_date_to = group.activity_date_to;
  }
  if (group?.activity_days_from_today !== undefined) {
    payload.activity_days_from_today = group.activity_days_from_today;
  }
  if (group?.virtual_top_up_from !== undefined) {
    payload.virtual_top_up_from = group.virtual_top_up_from;
  }
  if (group?.virtual_top_up_to !== undefined) {
    payload.virtual_top_up_to = group.virtual_top_up_to;
  }
  if (group?.contract_type !== undefined) {
    payload.contract_type = group.contract_type;
  }
  if (group?.app_type !== undefined) {
    payload.app_type = group.app_type;
  }

  return payload;
};

export function* getClientGroupsSaga({ params }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    const response = yield call(Api.getClientGroups, buildListParams(params));
    const items = (response.client_groups || []).map(mapListItem);
    const total = response.count || 0;
    const header = response.header || {};

    yield put(actions.clientGroups.setClientGroupsAction(items, total, header));
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* getClientGroupSaga({ id }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    const group = yield call(Api.getClientGroup, id);

    yield put(actions.clientGroups.setClientGroupAction(group || {}));
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* addClientGroupSaga({ body, history }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    const group = yield call(Api.addClientGroup, body);

    if (group?.id) {
      history.push(config.APP_PATHS.clientGroupDetails(group.id));
    }
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* editClientGroupSaga({ id, body, history }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    const group = yield call(Api.editClientGroup, id, body);

    if (group?.id) {
      history.push(config.APP_PATHS.clientGroupDetails(group.id));
    }
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* deactivateClientGroupSaga({ id, listParams, status }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    const group = yield call(Api.getClientGroup, id);
    const payload = buildDeactivatePayload(group, status ?? 0);

    yield call(Api.editClientGroup, id, payload);

    if (listParams) {
      const response = yield call(
        Api.getClientGroups,
        buildListParams(listParams)
      );
      const items = (response.client_groups || []).map(mapListItem);
      const total = response.count || 0;
      const header = response.header || {};
      yield put(
        actions.clientGroups.setClientGroupsAction(items, total, header)
      );
    }
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* syncClientGroupSaga({ id, listParams }) {
  try {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(true));

    yield call(Api.syncClientGroup, id);

    if (listParams) {
      const response = yield call(
        Api.getClientGroups,
        buildListParams(listParams)
      );
      const items = (response.client_groups || []).map(mapListItem);
      const total = response.count || 0;
      const header = response.header || {};
      yield put(
        actions.clientGroups.setClientGroupsAction(items, total, header)
      );
    }
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.clientGroups.setClientGroupsLoadingAction(false));
  }
}

export function* exportClientGroupsSaga({ filename, filterBody }) {
  try {
    yield put(actions.download.setXlsLoadingAction(true));
    const response = yield call(Api.exportClientGroups, filterBody);
    downloadFile(response, filename);
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.download.setXlsLoadingAction(false));
  }
}
